import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import axiosInstance from '../../api/axiosInstance';
import '../../styles/modal.css';
import '../../styles/version-history.css';
import { formatDateTime } from '../../utils/dateTimeFormat';

const TestCaseAutoUpdateModal = ({ isOpen, onClose, testCase, testCaseId, onApplyUpdates, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [applyingScript, setApplyingScript] = useState(false);
  const [healingData, setHealingData] = useState(null);
  const [error, setError] = useState(null);
  const [noHealingRequired, setNoHealingRequired] = useState(false);
  const [activeView, setActiveView] = useState('details'); // 'details' | 'script'


  const formatTimestamp = (timestamp) => formatDateTime(timestamp, { seconds: true, fallback: 'N/A' });

  const fetchSelfHealingAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      setNoHealingRequired(false);
      setHealingData(null);
            const response = await axiosInstance.post(
        `/self_healing/analyze_test_case_failure`,
        {
          test_case_id: testCaseId
        }
      );

// const params = new URLSearchParams();
// params.append("test_case_id", testCaseId);

// const response = await axiosInstance.post(
//   `${API_BASE_URL}/self_healing/analyze_test_case_failure`,
//   params,
//   {
//     headers: {
//       "Content-Type": "application/x-www-form-urlencoded"
//     }
//   }
// );
      if (response.status_code === 200 || response.status == "success") {
        // Check if healing is required
        if (!response?.analysis) {
          setNoHealingRequired(true);
          setHealingData(null);
        } else {
          setHealingData(response);
          setNoHealingRequired(false);
        }
      }
    } catch (err) {
      console.error('Error analyzing test case failure:', err);
      setError(err.response?.data?.message || err.message || 'Failed to analyze test case failure');
      setHealingData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && testCaseId) {
      fetchSelfHealingAnalysis();
    }
  }, [isOpen, testCaseId]);

  const handleApplyScript = async () => {
    if (!healingData) return;

    try {
      setApplyingScript(true);
      
      const response = await axiosInstance.post(
        `/self_healing/apply_script_fix`,
        {
          test_case_id: testCaseId,
          fixed_script_code: healingData.analysis?.fixed_script,
          explanation: healingData.analysis?.explanation,
        }
      );

      if (response.status_code === 200) {
        // Show success message and notify parent
        if (onSuccess) {
          onSuccess({
            message: response.data?.message,
            testCaseId: testCaseId
          });
        }
        
        // Close modal after success
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error applying script fix:', err);
      setError(err.response?.data?.message || err.message || 'Failed to apply script fix');
    } finally {
      setApplyingScript(false);
    }
  };


  if (!isOpen) return null;

  return (
<div data-rt-modal-overlay className="modal-overlay" style={{zIndex:17500}} onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
  {/* <div data-rt-modal-overlay className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose}></div> */}
  <div
    className="modal version-history-modal flex flex-col"
    style={{
      maxWidth: "1000px",
      width: "90%",
      height: "min(640px, 80vh)",
      borderRadius: "8px",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
      zIndex: 17600
    }}
  >
    <div className="bg-white rounded-lg shadow-2xl w-full h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-6 flex-shrink-0">
        <h2 className="text-2xl font-bold text-gray-900">
          {healingData?.message || (noHealingRequired ? 'Test Case Status' : 'Self-Healing Analysis')}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
          aria-label="Close modal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="overflow-y-auto p-6 flex-1 min-h-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 text-center">Analyzing test case failure...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
              <div className="flex items-center gap-3 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="text-lg font-semibold text-red-800">Error</h3>
              </div>
              <p className="text-red-700 text-sm mb-4">{error}</p>
              <button
                onClick={fetchSelfHealingAnalysis}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : noHealingRequired ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 max-w-md text-center">
              <div className="flex justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">No Healing Required</h3>
              <p className="text-green-700">The last execution PASSED. Your test case is working perfectly!</p>
            </div>
          </div>
        ) : healingData ? (
          <div className="flex flex-col h-full">
            {/* Test Case Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex-shrink-0">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Test Case ID: {healingData.test_case_id}</h3>
              <p className="text-sm text-blue-800">
                Failed at: <span className="font-medium">{formatTimestamp(healingData.failed_at)}</span>
              </p>
            </div>

            {/* Segmented toggle */}
            <div className="flex gap-2 border-b border-gray-200 mt-6 flex-shrink-0">
              <button
                onClick={() => setActiveView('details')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeView === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveView('script')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeView === 'script'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Fixed Script
              </button>
            </div>

            {/* Details view */}
            {activeView === 'details' && (
              <div className="space-y-6 mt-6 flex-1 min-h-0 overflow-y-auto">
                {/* Root Cause */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    Root Cause
                  </h3>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-gray-800 font-medium">{healingData.analysis?.root_cause}</p>
                  </div>
                </div>

                {/* Explanation */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    Explanation
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-700 text-sm leading-relaxed">{healingData.analysis?.explanation}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Script view */}
            {activeView === 'script' && (
              <div className="border border-gray-300 rounded-lg overflow-hidden mt-6 flex-1 min-h-0">
                <Editor
                  height="100%"
                  defaultLanguage="python"
                  theme="vs-dark"
                  value={healingData.analysis?.fixed_script || ''}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 12,
                    automaticLayout: true,
                    readOnly: true,
                    wordWrap: 'on',
                    folding: true,
                    lineNumbers: 'on'
                  }}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Footer */}
      {!loading && !error && !noHealingRequired && healingData && (
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyScript}
            disabled={applyingScript}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
          >
            {applyingScript ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Applying...
              </>
            ) : (
              <>
                Apply Script
              </>
            )}
          </button>
        </div>
      )}

      {/* Close button for no healing required / error states */}
      {!loading && (error || noHealingRequired) && (
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      )}
    </div>
  </div>
</div>
  );
};

export default TestCaseAutoUpdateModal;
